/*
 * Universal Knowledge Hub / Comparison Page Enhancements
 * File: assets/knowledge-hub.js
 *
 * Progressive enhancement only.
 * The page remains readable and indexable without JavaScript.
 */

(() => {
  'use strict';

  const root = document.querySelector('[data-knowledge-content]');
  if (!root) return;

  const tocContainer = document.querySelector('[data-generated-toc]');
  const progressBar = document.querySelector('[data-reading-progress]');
  const backToTop = document.querySelector('[data-back-to-top]');

  const slugify = (value) =>
    value
      .toLowerCase()
      .trim()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

  const ensureUniqueId = (heading, usedIds) => {
    if (heading.id) {
      usedIds.add(heading.id);
      return heading.id;
    }

    const base = slugify(heading.textContent) || 'section';
    let candidate = base;
    let index = 2;

    while (usedIds.has(candidate) || document.getElementById(candidate)) {
      candidate = `${base}-${index}`;
      index += 1;
    }

    heading.id = candidate;
    usedIds.add(candidate);
    return candidate;
  };

  /* -------------------------------------------------------
     Wrap tables for mobile scrolling
     ------------------------------------------------------- */

  root.querySelectorAll('table').forEach((table) => {
    if (table.closest('.kh-table-wrap, .xdeep-table-wrap')) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'kh-table-wrap';
    table.parentNode.insertBefore(wrapper, table);
    wrapper.appendChild(table);
  });

  /* -------------------------------------------------------
     Heading anchors and generated table of contents
     ------------------------------------------------------- */

  const usedIds = new Set();
  const headings = [...root.querySelectorAll('h2, h3')].filter(
    (heading) =>
      !heading.closest('.kh-card, .xdeep-card, .kh-product-card, footer')
  );

  const tocList = document.createElement('ol');

  headings.forEach((heading) => {
    const id = ensureUniqueId(heading, usedIds);

    if (!heading.querySelector('.kh-heading-anchor')) {
      const anchor = document.createElement('a');
      anchor.className = 'kh-heading-anchor';
      anchor.href = `#${id}`;
      anchor.setAttribute(
        'aria-label',
        `Link to ${heading.textContent.trim()}`
      );
      anchor.textContent = '#';
      heading.appendChild(anchor);
    }

    const item = document.createElement('li');
    if (heading.tagName === 'H3') item.className = 'kh-toc__subitem';

    const link = document.createElement('a');
    link.href = `#${id}`;
    link.textContent = heading.childNodes[0]?.textContent?.trim() || heading.textContent.trim();
    link.dataset.tocTarget = id;

    item.appendChild(link);
    tocList.appendChild(item);
  });

  if (tocContainer && headings.length > 1) {
    tocContainer.appendChild(tocList);
  } else {
    document.querySelector('[data-toc-panel]')?.setAttribute('hidden', '');
  }

  /* -------------------------------------------------------
     Open FAQ item when directly linked
     ------------------------------------------------------- */

  const openHashedDetails = () => {
    const id = window.location.hash.slice(1);
    if (!id) return;

    const target = document.getElementById(id);
    const details = target?.closest('details');
    if (details) details.open = true;
  };

  openHashedDetails();
  window.addEventListener('hashchange', openHashedDetails);

  /* -------------------------------------------------------
     External link hardening
     ------------------------------------------------------- */

  root.querySelectorAll('a[target="_blank"]').forEach((link) => {
    const rel = new Set(
      (link.getAttribute('rel') || '').split(/\s+/).filter(Boolean)
    );
    rel.add('noopener');
    rel.add('noreferrer');
    link.setAttribute('rel', [...rel].join(' '));
  });

  /* -------------------------------------------------------
     Reading progress and back-to-top
     ------------------------------------------------------- */

  const updateProgress = () => {
    if (!progressBar) return;

    const rect = root.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const total = Math.max(root.offsetHeight - viewportHeight, 1);
    const travelled = Math.min(Math.max(-rect.top, 0), total);
    progressBar.style.width = `${(travelled / total) * 100}%`;
  };

  const updateBackToTop = () => {
    if (!backToTop) return;
    backToTop.hidden = window.scrollY < 700;
  };

  backToTop?.addEventListener('click', () => {
    window.scrollTo({top: 0, behavior: 'smooth'});
  });

  /* -------------------------------------------------------
     Active TOC item
     ------------------------------------------------------- */

  const tocLinks = [...document.querySelectorAll('[data-toc-target]')];

  const setActiveToc = () => {
    if (!tocLinks.length) return;

    const offset = 160;
    let currentId = headings[0]?.id || '';

    headings.forEach((heading) => {
      if (heading.getBoundingClientRect().top <= offset) {
        currentId = heading.id;
      }
    });

    tocLinks.forEach((link) => {
      if (link.dataset.tocTarget === currentId) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  let ticking = false;

  const onScroll = () => {
    if (ticking) return;

    window.requestAnimationFrame(() => {
      updateProgress();
      updateBackToTop();
      setActiveToc();
      ticking = false;
    });

    ticking = true;
  };

  updateProgress();
  updateBackToTop();
  setActiveToc();

  window.addEventListener('scroll', onScroll, {passive: true});
  window.addEventListener('resize', updateProgress);

  /* -------------------------------------------------------
     Smooth in-page navigation
     ------------------------------------------------------- */

  root.addEventListener('click', (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;

    const id = link.getAttribute('href').slice(1);
    const target = document.getElementById(id);
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({behavior: 'smooth', block: 'start'});
    history.pushState(null, '', `#${id}`);
  });

  /* -------------------------------------------------------
     Optional FAQ: allow only one item open within .kh-faq
     Add data-single-open to the FAQ wrapper to enable.
     ------------------------------------------------------- */

  root.querySelectorAll('.kh-faq[data-single-open], .xdeep-faq[data-single-open]')
    .forEach((faq) => {
      faq.addEventListener('toggle', (event) => {
        const opened = event.target;
        if (!(opened instanceof HTMLDetailsElement) || !opened.open) return;

        faq.querySelectorAll('details[open]').forEach((details) => {
          if (details !== opened) details.open = false;
        });
      }, true);
    });
})();
